import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useGetNickName } from "../model";
import { Flex, Input, Modal, Upload, UploadFile } from "antd";
import { ReactComponent as Camera } from "../../../assets/images/icon/camera.svg";
import { ReactComponent as Img } from "../../../assets/images/icon/img.svg";
import { CircleCloseIcon } from "../../../shared/icon";
import { getLocalAccessToken } from "../../../service";
import { useCreateFeedWeb } from "./model";
import { useQueryClient } from "@tanstack/react-query";
import { TopBar } from "../../../widgets";
import { CustomToast, DividerThin, SolidBtn, useSafeVh } from "../../../shared";
import { isIOS } from "react-device-detect";
import { RcFile, UploadChangeParam } from "antd/es/upload";
import Lottie from "lottie-react";
import LoadingBig from "../../../assets/images/lottie/loading_big.json";
import { useMyContext } from "../../../entities/context";
import { androidOnInputFocus } from "../../../utils";
import "./index.css";

const CreateFeed = () => {
  const { topHeight } = useMyContext();
  const [cancelOpen, setCancelOpen] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initForm = searchParams.get("initForm");
  const { feedPlaceholder } = useMyContext();
  const { data: nickName, isPending } = useGetNickName(true);
  const [content, setContent] = useState<string>("");
  const [fileList, setFileList] = useState<UploadFile<any>[]>([]);
  const createFeedWeb = useCreateFeedWeb();
  const photoRef = useRef<HTMLDivElement>(null);
  const cameraRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [checkFocus, setCheckFocus] = useState<boolean>(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isShowToast, setIsShowToast] = useState<boolean>(false);
  const [visualVieHeight, setVisualVieHeight] = useState<number>();
  const [imgHeight, setImgHeight] = useState<number>();

  const handleChange = (info: UploadChangeParam<UploadFile<any>>) => {
    setFileList(info.fileList);
  };

  const onClickUploadFeedButton = () => {
    if (content.trim() === "" && fileList.length === 0) {
      return;
    }

    createFeedWeb.mutate(
      { content, imageUrlList: fileList.map((q) => q.response) },
      {
        onSuccess: (res) => {
          if (res.data) {
            queryClient.invalidateQueries({ queryKey: ["nickName"] });
            navigate("/community", { state: { createFeedId: res.data } });
          }
        },
      }
    );
  };

  const handleBeforeUpload = (file: RcFile) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      setIsShowToast(true);
    }
    return isImage || Upload.LIST_IGNORE;
  };

  const containerRefHandleScroll = () => {
    if (!checkFocus) return;
    document.getElementById("containerRef")?.scrollIntoView({
      block: "end",
      behavior: "smooth",
    });
  };

  const getMaxHeight = (custom?: string) => {
    const focusAndIos = checkFocus ? 0 : isIOS ? 34 : 0;

    return `calc(${visualVieHeight ? visualVieHeight + "px" : "100vh"
      } - 60px - 48px - ${topHeight}px - ${focusAndIos}px - ${custom ?? "0px"})`;
  };

  useEffect(() => {
    if (!initForm) return;
    initForm === "photo" && photoRef.current && photoRef.current.click();
    initForm === "camera" && cameraRef.current && cameraRef.current.click();
  }, [initForm]);

  useEffect(() => {
    if (isPending) return;
    if (!nickName) return navigate(-1);
  }, [isPending]);

  useEffect(() => {
    setImgHeight(window.innerHeight / 4)
    if (inputRef.current && initForm !== "photo" && initForm !== "camera") {
      inputRef.current?.focus();
      androidOnInputFocus(true);
      setCheckFocus(true);
    }
  }, []);

  useEffect(() => {
    if (scrollRef.current && fileList.length > 0) {
      scrollRef.current.scrollTo({
        left: scrollRef.current.scrollWidth,
        behavior: "smooth",
      });
    }
  }, [fileList]);

  useEffect(() => {
    const handleVisualViewportResize = () => {
      if (window.visualViewport) {
        const viewportHeight = window.visualViewport.height;
        setVisualVieHeight(viewportHeight);
        const windowInnerHeight = window.innerHeight;
        const keyboardHeight = windowInnerHeight - viewportHeight;
        if (keyboardHeight > 0) {
          document.body.style.height = `${viewportHeight}px`;
        } else {
          document.body.style.height = "100%";
        }
      }
    };

    window.addEventListener("scroll", containerRefHandleScroll);

    if (window.visualViewport) {
      window.visualViewport.onresize = handleVisualViewportResize;
    }
    return () => {
      window.removeEventListener("scroll", containerRefHandleScroll);
    };
  }, [checkFocus]);

  useEffect(() => {
    const updateVh = () => {
      const vh = window.visualViewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty('--vh', `${vh * 0.01}px`);
    };

    updateVh(); // 초기 설정

    window.visualViewport?.addEventListener('resize', updateVh);
    window.addEventListener('resize', updateVh); // fallback

    return () => {
      window.visualViewport?.removeEventListener('resize', updateVh);
      window.removeEventListener('resize', updateVh);
    };
  }, []);

  return (
    <>
      <TopBar
        type="closeIcon"
        title="글쓰기"
        overOnClicks={{
          left: () => {
            setCancelOpen(true);
          },
        }}
        rightIcon={false}
        position="fixed"
        thema="dark"
      />
      <div
        style={{
          height: "100vh",
          maxHeight: getMaxHeight(),
          overflowY: "scroll",
        }}
        onClick={() => inputRef.current?.focus()}
      >
        <div style={{ padding: "23px 20px 8px" }}>
          <div className="sub-title/st2" style={{ marginBottom: "8px" }}>
            {nickName}
          </div>
          <Input.TextArea
            className={"body/b3 secondary/s850 community-create-text-area"}
            autoSize
            ref={inputRef}
            variant="borderless"
            placeholder={feedPlaceholder}
            maxLength={400}
            style={{
              padding: 0,
              maxHeight: getMaxHeight(
                `(31px + 25px + ${fileList.length > 0 ? "25vh" : "0px"})`
              ),
              overflowY: "scroll",
            }}
            onChange={(e) => setContent(e.target.value)}
            value={content}
            onFocus={() => {
              setCheckFocus(true);
              androidOnInputFocus(true);
            }}
            onBlur={() => {
              setCheckFocus(false);
              androidOnInputFocus(false);
            }}
          />
        </div>
        <Flex
          style={{ overflowX: "scroll", paddingRight: "20px" }}
          ref={scrollRef}
        >
          {fileList.map((file, idx, arr) => (
            <div
              key={idx}
              style={{
                position: "relative",
                marginRight: idx === arr.length - 1 ? "20px" : "12px",
                marginLeft: idx === 0 ? "20px" : 0,
              }}
            >
              {file.status !== "done" ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 162,
                    height: imgHeight,
                    border: "1px solid #0000000D",
                    borderRadius: "4px",
                    backgroundColor: "var(--secondarys100)",
                  }}
                >
                  <Lottie
                    style={{ width: 24, height: 24 }}
                    animationData={LoadingBig}
                    autoplay={true}
                  />
                </div>
              ) : (
                <>
                  <CircleCloseIcon
                    fillOpacity="1"
                    style={{ position: "absolute", right: 12, top: 12 }}
                    width={24}
                    height={24}
                    fill1="var(--baseblack)"
                    fill2="var(--basewhite)"
                    onClick={() => {
                      setFileList((curr) =>
                        curr.filter((q) => q.uid !== file.uid)
                      );
                    }}
                  />
                  <img
                    src={file.response}
                    alt="업로드된 이미지"
                    width={"auto"}
                    style={{
                      display: "block",
                      height: imgHeight,
                      borderRadius: 4,
                    }}
                  />
                </>
              )}
            </div>
          ))}
        </Flex>
      </div>
      <div
        id="containerRef"
        style={{
          position: "absolute",
          backgroundColor: "white",
          paddingBottom: isIOS ? (checkFocus ? 0 : 34) : 0,
          width: "100%",
          maxWidth: "480px",
          bottom: 0,
        }}
      >
        <DividerThin />
        <Flex style={{ padding: "12px 20px", justifyContent: "space-between" }}>
          <Flex style={{ gap: 8 }}>
            <Upload
              className="custom-upload-children"
              name="multipartFile"
              action={`${process.env.REACT_APP_API_DOMAIN}/s3/upload/web`}
              headers={{ Authorization: `Bearer ${getLocalAccessToken()}` }}
              withCredentials={true}
              itemRender={() => <div></div>}
              onChange={handleChange}
              fileList={fileList}
              maxCount={10}
              multiple
              accept="image/*"
              disabled={fileList.length === 10}
              beforeUpload={handleBeforeUpload}
            >
              <div ref={photoRef} onMouseDown={(e) => e.preventDefault()}>
                <Img
                  width={24}
                  height={24}
                  stroke={
                    fileList.length === 10
                      ? "var(--secondarys200)"
                      : "var(--secondarys500)"
                  }
                  style={{ display: "block", padding: "6px" }}
                />
              </div>
            </Upload>

            <Upload
              className="custom-upload-children"
              name="multipartFile"
              action={`${process.env.REACT_APP_API_DOMAIN}/s3/upload/web`}
              headers={{ Authorization: `Bearer ${getLocalAccessToken()}` }}
              withCredentials={true}
              itemRender={() => <div></div>}
              onChange={handleChange}
              fileList={fileList}
              maxCount={10}
              multiple
              capture="environment"
              accept="image/*"
              disabled={fileList.length === 10}
            >
              <div ref={cameraRef} onMouseDown={(e) => e.preventDefault()}>
                <Camera
                  width={24}
                  height={24}
                  stroke={
                    fileList.length === 10
                      ? "var(--secondarys200)"
                      : "var(--secondarys500)"
                  }
                  style={{ display: "block", padding: "6px" }}
                />
              </div>
            </Upload>
          </Flex>
          <Flex style={{ alignItems: "center" }} gap={12}>
            <div className="caption/c1 secondary/s400">
              {content.length}/400
            </div>
            <SolidBtn
              state={
                content.trim() === "" && fileList.length === 0
                  ? "disabled"
                  : "default"
              }
              type="solid"
              icon={false}
              onClick={onClickUploadFeedButton}
              style={{ padding: "var(--space8) var(--space20)" }}
            >
              게시
            </SolidBtn>
          </Flex>
        </Flex>
      </div>
      <Modal
        centered
        styles={{
          content: {
            width: "auto",
            marginInline: 12,
            padding: "20px 16px",
            borderRadius: 4,
          },
        }}
        open={cancelOpen}
        onCancel={() => setCancelOpen(false)}
        closeIcon={null}
        footer={null}
      >
        <div>
          <div style={{ textAlign: "center" }}>
            <div className="sub-title/st5 base/black">작성 취소</div>
            <div className="body/b2 secondary/s800" style={{ marginTop: 8 }}>
              <div>글을 저장하지 않고 나가시겠습니까?</div>
              <div>작성한 글은 저장되지 않습니다.</div>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              gap: 16,
              justifyContent: "center",
              marginTop: 16,
            }}
          >
            <SolidBtn
              type="line"
              style={{
                width: "100%",
                border: "solid 1px var(--secondarys400)",
                color: "var(--secondarys600)",
              }}
              icon={false}
              onClick={() => setCancelOpen(false)}
            >
              돌아가기
            </SolidBtn>
            <SolidBtn
              type="solid"
              style={{ width: "100%" }}
              icon={false}
              onClick={() => {
                setCancelOpen(false);
                navigate(-1);
              }}
            >
              작성 취소
            </SolidBtn>
          </div>
        </div>
      </Modal>
      {isShowToast && (
        <CustomToast
          setToast={setIsShowToast}
          text={"이미지만 업로드 가능합니다"}
          style={{ backgroundColor: "var(--dimd300)" }}
        />
      )}
    </>
  );
};

export default CreateFeed;
